import React, { useEffect, useState } from 'react'
import client from '../../client/Client'
import HeaderBanner from '../ui/HeaderBanner'

interface TableRow {
  Year: string;
  Citations: string;
  Authors: string;
  Paper: string;
  Conference: string;
  Country: string;
}

interface TopCitedData {
  SSDinScopus: any[];
  BestCitedPapersPerYear: TableRow[];
  TopFive: TableRow[];
}

const TopCitedPapers = () => {
  const [data, setData] = useState<TopCitedData>()

  useEffect(() => {
    client.fetch('*[_type == "TopCitedPapers"]')
      .then((result) => {
        setData(result[0]);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, [])
  const TABLE_HEAD = ["Year", "Citations", "Authors", "Paper", "Conference", "Country"];

const TableComponent = ({ rows, title }: { rows: TableRow[]; title: string }) => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
      <h3 className="text-base sm:text-lg font-semibold text-gray-800">{title}</h3>
    </div>

    {/* ✅ Desktop Table */}
    <div className="hidden md:block">
      <table className="w-full table-auto text-left">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {TABLE_HEAD.map((head) => (
              <th
                key={head}
                className="px-4 sm:px-6 py-2 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows?.map((row, index) => (
            <tr
              key={index}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              <td className="px-4 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium text-gray-900">
                {row.Year}
              </td>
              <td className="px-4 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-blue-901 font-semibold">
                {row.Citations}
              </td>
              <td className="px-4 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-700 max-w-xs break-words">
                {row.Authors}
              </td>
              <td className="px-4 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-700 max-w-md break-words">
                {row.Paper}
              </td>
              <td className="px-4 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-600 whitespace-normal break-words">
                {row.Conference}
              </td>
              <td className="px-4 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-600 whitespace-normal break-words">
                {row.Country}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* ✅ Mobile Card Layout */}
    <div className="block md:hidden">
      <div className="divide-y divide-gray-200">
        {rows?.map((row, index) => (
          <div
            key={index}
            className="p-4 bg-white hover:bg-gray-50 transition rounded-none"
          >
            <div className="flex justify-between text-xs font-semibold text-gray-500 uppercase mb-1">
              <span>{row.Year}</span>
              <span className="text-blue-901">{row.Citations} citations</span>
            </div>

            <div className="text-sm font-semibold text-gray-800 mb-1">
              {row.Paper}
            </div>

            <div className="text-xs text-gray-600 mb-1">
              <span className="font-semibold text-gray-700">Authors: </span>
              {row.Authors}
            </div>

            <div className="text-xs text-gray-600 mb-1">
              <span className="font-semibold text-gray-700">Conference: </span>
              {row.Conference}
            </div>

            <div className="text-xs text-gray-600">
              <span className="font-semibold text-gray-700">Country: </span>
              {row.Country}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);


  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
      <div className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-1" aria-hidden="true">
        <svg width="1360" height="578" viewBox="0 0 1360 578" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="illustration-01">
              <stop stopColor="#FFF" offset="0%" />
              <stop stopColor="#EAEAEA" offset="77.402%" />
              <stop stopColor="#DFDFDF" offset="100%" />
            </linearGradient>
          </defs>
          <g fill="url(#illustration-01)" fillRule="evenodd">
            <circle cx="1232" cy="128" r="128" />
            <circle cx="155" cy="443" r="64" />
          </g>
        </svg>
      </div>
      
      <HeaderBanner title="Top Cited Papers" />
      
      <div className="text-left space-y-8 pt-12">
        {/* SSD in Scopus Section */}
        <div className="bg-gray-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
          <h2 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-4">
            SSD in Scopus
          </h2>
          <div className="text-gray-700 leading-relaxed p-2">
            {data?.SSDinScopus[0]?.children.map((item: any, index: number) => {
              const linkData = data.SSDinScopus[0].markDefs.find(
                (def: any) => def._key === item.marks[0]
              );

              // 🧠 Condition 1: Linked text
              if (linkData) {
                return (
                  <a
                    key={index}
                    href={linkData.href}
                    className="text-blue-901 hover:text-blue-800 font-medium underline transition-colors duration-200"
                  >
                    {item.text}
                  </a>
                );
              }

              // 🧠 Condition 2: Bold (strong) text
              if (item.marks?.[0] === "strong") {
                return (
                  <strong key={index} className="font-bold text-gray-800">
                    {item.text}
                  </strong>
                );
              }

              // 🧠 Default: Normal text
              return <span key={index}>{item.text}</span>;
            })}
          </div>
        </div>

        {/* Best Cited Papers Per Year Section */}
        <div className="bg-gray-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
          <h2 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-6">
            Best Cited Papers Per Year
          </h2>
          <TableComponent 
            rows={data?.BestCitedPapersPerYear || []} 
            title="Annual Citation Leaders"
          />
        </div>

        {/* Top 5 Best Cited Papers Section */}
        <div className="bg-gray-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
          <h2 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-6">
            Top 5 Best Cited Papers in SSD History
          </h2>
          <TableComponent 
            rows={data?.TopFive || []} 
            title="All-Time Citation Leaders"
          />
        </div>

        {/* Additional Information Section */}
        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Citation Information
          </h3>
          <p className="text-gray-700">
            Citation data is sourced from Scopus and updated regularly. For questions about citation metrics, please contact:{' '}
            <a className='text-blue-901 hover:text-blue-800 font-medium underline' href='mailto:info@ssd-multiconf.org'>
              info@ssd-multiconf.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopCitedPapers;